import { find, isEmpty, includes, partition, unionBy, without } from 'lodash';

import * as ActionTypes from '../actions/types/SeatingChartActionTypes';

const initialState = {
  busy: false,
  byEventId: {},
  events: [],
  seatingChart: {},
  tablesByUuid: {},
  tables: [],
  seatsByGuestDistinction: {},
  onboarding: false,
  step: 0,
  seatsReorderedTable: null,
  event: {},
  updateSeatsRequest: [],
  selectedGuests: { guests: [], couple: [] },
  draggingSeatInProgress: false,
  activeSeatInput: null,
  guestSearchSet: {},
  seatsByUUID: {}
};

const seatingChartReducer = (state = initialState, action) => {
  switch (action.type) {
    // busy
    case ActionTypes.REQUEST_CHART: {
      return { ...state, busy: true };
    }
    // busy
    case ActionTypes.REQUEST_CHARTS: {
      return { ...state, busy: true };
    }
    // busy, seatingChart, tablesByUuid, tables, seatsByGuestDistinction, guestSearchSet, seatsByUUID
    case ActionTypes.RECEIVE_CHART: {
      const seatingChart = action.payload.data;
      const seatingChartTables = action.payload.data.tables;
      const tablesByUuid = {};
      const tables = [];
      // const seatsByGuestDistinction = { guests: {}, couple: {} };

      // construct guestSearchSet
      const guestGroups = action.payload.data.guests.guest_list;
      const guestSearchSet = Object.keys(guestGroups)
        .reduce((acc, key) => acc.concat(guestGroups[key]), []) // <- create array of groups
        .reduce((acc, group) => {
          // <- concat all the guests
          const affiliation = group.guest_affiliation;
          const guests = group.guests.map(
            // <- shape each guest entry
            guest => ({
              [guest.guest_view.id]: {
                affiliation,
                displayName: guest.display_name,
                groupID: guest.guest_view.guest_group_id,
                id: guest.guest_view.id,
                seatUUID: guest.seat_uuid,
                tableUUID: guest.table_uuid
              }
            })
          );
          return acc.concat(guests);
        }, [])
        .reduce((acc, obj) => ({ ...acc, ...obj }), {}); // <- place into obj, not array

      // seatsByUUID
      const seatsByUUID = seatingChartTables
        .reduce((acc, table) => [...acc, ...table.seats], [])
        .reduce((acc, seat) => ({
          ...acc,
          [seat.uuid]: { ...seat, isEmpty: (seat.couple_role === null && seat.guest_id === null) }
        }), {});

      // seatsByGuestDistinction
      const filledSeats = Object.values(seatsByUUID).filter(seat => seat.couple_role !== null || seat.guest_id !== null);
      const [coupleSeats, guestSeats] = partition(filledSeats, seat => seat.couple_role !== null);
      const seatsByGuestDistinction = {
        couple: coupleSeats.reduce((acc, seat) => ({ ...acc, [seat.uuid]: seat }), {}),
        guests: guestSeats.reduce((acc, seat) => ({ ...acc, [seat.uuid]: seat }), {})
      };

      // tables & tableByUuid (update isEmpty seat properties)
      if (seatingChartTables.length > 0) {
        seatingChartTables.forEach((table) => {
          const newSeats = table.seats.map(seat => ({ ...seat, isEmpty: seat.couple_role === null && seat.guest_id === null }));
          const newTable = {
            ...table,
            num_filled_seats: table.seats.filter(seat => seat.isEmpty).length,
            seats: newSeats
          };
          tablesByUuid[table.uuid] = { table: newTable, index: tables.length };
          tables.push(newTable);
        });
      }

      return {
        ...state,
        busy: false,
        guestSearchSet,
        seatingChart,
        seatsByGuestDistinction,
        seatsByUUID,
        tables,
        tablesByUuid
      };
    }
    // busy, byEventId, events
    case ActionTypes.RECEIVE_CHARTS: {
      const events = [];
      const seatingCharts = action.payload;
      const byEventId = { ...seatingCharts.event_to_charts_map };

      Object.keys(byEventId).forEach((key) => {
        events.push({
          event: key,
          uuid: byEventId[key][0].uuid,
          name: byEventId[key][0].name
        });
      });

      return {
        ...state,
        busy: false,
        byEventId,
        events
      };
    }
    // tables, tablesByUuid
    case ActionTypes.REORDER_TABLES: {
      if (state.tables.length <= 0) return state;
      const { sourceIndex } = action.payload;
      const { hoverIndex } = action.payload;
      const tables = [...state.tables];
      const sourceTable = tables[sourceIndex];

      tables.splice(sourceIndex, 1); // removing what you are dragging
      tables.splice(hoverIndex, 0, sourceTable); // inserting it into hoverIndex

      const tablesByUuid = {};

      // Need to update display orders of any changed
      tables.forEach((currentTable, index) => {
        tablesByUuid[currentTable.uuid] = { table: currentTable, index };
      });
      const newTables = tables.map(
        (currentTable, index) => ({ ...currentTable, display_order: index + 1 })
      );
      return {
        ...state,
        tables: newTables,
        tablesByUuid
      };
    }
    // seatsByGuestDistinction, tables, tablesByUUID,;
    // TODO: guestSearchSet, seatsByUUID
    case ActionTypes.REORDER_SEATS: {
      const { forceReorder, hoverIndex, sourceIndex, tableUuid: tableUUID } = action.payload;
      const { seatsReorderedTable, tablesByUuid } = state;
      const { index, table } = tablesByUuid[tableUUID];
      if (tablesByUuid[tableUUID] === null || table === null) return state;
      const tables = state.tables.slice();
      const sourceTable = forceReorder ? seatsReorderedTable : table;
      const sourceSeats = sourceTable.seats.slice();
      const sourceSeat = sourceSeats[sourceIndex];

      const newSeats = sourceSeats.filter((currSeat, i) => i !== sourceIndex);
      newSeats.splice(hoverIndex, 0, sourceSeat);
      const newerSeats = newSeats.map((currSeat, i) => ({ ...currSeat, display_order: i + 1 }));

      // compose return values
      const newTable = { ...sourceTable, seats: newerSeats };
      const newTables = tables.map((currentTable, i) => (i === index ? newTable : currentTable));
      const newSeatsByGuestDistinction = newerSeats.reduce((acc, currSeat) => {
        const { couple, guests } = acc;
        const role = currSeat.couple_role;
        const guestID = currSeat.guest_id;
        if (currSeat.couple_role !== null) {
          return { guests, couple: { ...couple, [role]: currSeat } };
        }
        return { couple, guests: { ...guests, [guestID]: currSeat } };
      }, { guests: {}, couple: {} });

      return {
        ...state,
        tables: newTables,
        tablesByUuid: {
          ...tablesByUuid,
          [newTable.uuid]: { table: newTable, index }
        },
        seatsByGuestDistinction: newSeatsByGuestDistinction,
      };
    }
    // seatsReorderedTable, updateSeatsRequest
    // TODO: + guestSearchSet, seatsByUUID
    case ActionTypes.REORDER_SEATS_IN_TABLE: {
      const updateSeatsRequest = action.payload.table.seats;
      return {
        ...state,
        updateSeatsRequest,
        seatsReorderedTable: null
      };
    }
    // seatsByGuestDistinction, tables, tablesByUUID, updateSeatsRequest, guestSearchSet, seatsByUUID
    case ActionTypes.ADD_PEOPLE_TO_TABLE: {
      const { guestsByGuestDistinction, tableUuid } = action.payload;
      const {
        guestSearchSet,
        selectedGuests: selectedPeople,
        seatsByGuestDistinction,
        seatsByUUID,
        tables,
        tablesByUuid
      } = state;
      const { couple: selectedCouple, guests: selectedGuests } = selectedPeople;
      const selectedPeopleSet = selectedCouple.concat(selectedGuests).filter(key => key !== undefined);
      const { table: targetTable } = state.tablesByUuid[tableUuid];
      // check for enough empty seats
      const emptySeats = targetTable.seats.filter(seat => seat.isEmpty);
      const dragCount = selectedCouple.length + selectedGuests.length;
      if (dragCount === 0
        || tableUuid === null
        || emptySeats.length === 0
        || (tableUuid !== null && dragCount > emptySeats.length)
      ) return state;
      // check that target != source
      const isSourceTarget = selectedPeopleSet.reduce((acc, personKey) => {
        const distinction = (typeof personKey === 'string') ? 'couple' : 'guests';
        if (seatsByGuestDistinction[distinction][personKey] === undefined) return false;
        return state.seatsByGuestDistinction[distinction][personKey].table_uuid === tableUuid;
      }, false);
      if (isSourceTarget) return state;
      // compositional-data
      let selectedIndex = 0;
      const newSeats = targetTable.seats.map((seat) => {
        if (selectedIndex < selectedPeopleSet.length && seat.isEmpty) {
          const personKey = selectedPeopleSet[selectedIndex];
          const distinction = (typeof personKey === 'string') ? 'couple' : 'guests';
          const displayName = guestsByGuestDistinction[distinction][personKey].display_name;
          const coupleRole = distinction === 'couple' ? personKey : null;
          const guestID = distinction === 'guests' ? personKey : null;
          selectedIndex += 1;
          return { ...seat, display_name: displayName, couple_role: coupleRole, guest_id: guestID, isEmpty: false };
        }
        return seat;
      });
      const filledSeatsByUUID = selectedPeopleSet.reduce((acc, personKey) => {
        const distinction = (typeof personKey === 'string') ? 'couple_role' : 'guest_id';
        const matchingSeat = find(newSeats, seat => seat[distinction] === personKey);
        return { ...acc, [matchingSeat.uuid]: matchingSeat };
      }, {});
      const clearedSeatsByUUID = selectedPeopleSet.map((personKey) => {
        const distinction = (typeof personKey === 'string') ? 'couple' : 'guests';
        return guestsByGuestDistinction[distinction][personKey].seat_uuid;
      }).filter(seatUUID => seatUUID !== null)
        .reduce((acc, seatUUID) => {
          const clearedSeat = {
            ...seatsByUUID[seatUUID], guest_id: null, couple_role: null, display_name: null, isEmpty: true
          };
          return { ...acc, [seatUUID]: clearedSeat };
        }, {});
      const searchSetUpdates = Object.keys(filledSeatsByUUID).reduce((acc, seatUUID) => {
        const seat = filledSeatsByUUID[seatUUID];
        if (seat.guest_id === null) return acc;
        const searchEntry = guestSearchSet[seat.guest_id];
        return { ...acc, [seat.guest_id]: { ...searchEntry, seatUUID, tableUuid } };
      }, {});
      const clearedSeatsByTableUUID = Object.values(clearedSeatsByUUID).reduce(
        (acc, seat) => ({ ...acc, [seat.table_uuid]: [...acc[seat.table_uuid], seat] }), {}
      );
      const updatedSeatsByTableUUID = Object.keys(clearedSeatsByTableUUID).reduce(
        (acc, tableUUID) => {
          const oldSeats = tablesByUuid[tableUUID].table.seats;
          const clearedSeats = clearedSeatsByTableUUID[tableUUID];
          const seats = unionBy(clearedSeats, oldSeats, 'uuid');
          return { ...acc, [tableUUID]: seats };
        }, {}
      );
      // compose return values
      const newSeatsByGuestDistinction = selectedPeopleSet.reduce(
        (acc, personKey) => {
          const distinction = (typeof personKey === 'string') ? 'couple_role' : 'guest_id';
          const matchingSeat = find(newSeats, seat => seat[distinction] === personKey);
          return { ...acc, [distinction]: { ...acc[distinction], [personKey]: matchingSeat } };
        }, { couple: {}, guests: {} }
      );
      const newTablesByUUID = Object.keys(tablesByUuid).reduce(
        (acc, UUID) => {
          if (UUID === tableUuid) return { ...acc, [UUID]: { ...tablesByUuid[UUID], seats: newSeats } };
          if (updatedSeatsByTableUUID[UUID]) return { ...acc, [UUID]: { ...tablesByUuid[UUID], seats: updatedSeatsByTableUUID[UUID] } };
          return { ...acc, [UUID]: tablesByUuid[UUID] };
        }, {}
      );
      const newTables = tables.map((table) => {
        if (table.uuid === tableUuid) return { ...table, seats: newSeats };
        if (updatedSeatsByTableUUID[table.uuid]) return { ...table, seats: updatedSeatsByTableUUID[table.uuid] };
        return table;
      });
      const newSeatsByUUID = {
        ...seatsByUUID,
        ...filledSeatsByUUID,
        ...clearedSeatsByUUID
      };
      const updateSeatsRequest = Object.values(filledSeatsByUUID).concat(Object.values(clearedSeatsByUUID));
      return {
        ...state,
        seatsByGuestDistinction: {
          couple: {
            ...seatsByGuestDistinction.couple,
            ...newSeatsByGuestDistinction.couple
          },
          guests: {
            ...seatsByGuestDistinction.guests,
            ...newSeatsByGuestDistinction.guests
          }
        },
        tables: newTables,
        tablesByUuid: newTablesByUUID,
        updateSeatsRequest,
        guestSearchSet: {
          ...guestSearchSet,
          ...searchSetUpdates
        },
        seatsByUUID: newSeatsByUUID
      };
    }
    // seatsByGuestDistinction, tables, tablesByUUID, updateSeatsRequest, guestSearchSet, seatsByUUID
    case ActionTypes.ADD_PERSON_TO_SEAT: {
      const { newSeat } = action.payload;
      const { guest_id: guestUUID, table_uuid: tableUUID, uuid: seatUUID } = newSeat;
      const { table } = state.tablesByUuid[tableUUID];
      const tableSeats = state.tablesByUuid[tableUUID].table.seats;
      const newSeatsArray = tableSeats.map((tableSeat) => {
        if (tableSeat.uuid === newSeat.uuid) return newSeat;
        return tableSeat;
      });
      const newTable = { ...table, seats: newSeatsArray };
      const distinctionKey = guestUUID === null ? 'couple' : 'guests';
      const occupantKey = guestUUID === null ? newSeat.couple_role : guestUUID;
      const newDistinctionGroup = {
        ...state.seatsByGuestDistinction[distinctionKey],
        [occupantKey]: newSeat
      };

      // compose the return values
      const tables = state.tables.map((currentTable) => {
        if (currentTable.uuid === tableUUID) return newTable;
        return currentTable;
      });
      const tablesByUuid = { ...state.tablesByUuid, [tableUUID]: newTable };
      const seatsByGuestDistinction = {
        ...state.seatsByGuestDistinction,
        [distinctionKey]: newDistinctionGroup
      };
      const updateSeatsRequest = [newSeat];
      const guestSearchSet = {
        ...state.guestSearchSet,
        [occupantKey]: {
          ...state.guestSearchSet[occupantKey], seatUUID, tableUUID
        }
      };
      const seatsByUUID = { ...state.seatsByUUID, [seatUUID]: newSeat };
      return {
        ...state,
        seatsByGuestDistinction,
        tables,
        tablesByUuid,
        updateSeatsRequest,
        guestSearchSet,
        seatsByUUID
      };
    }
    // seatsByGuestDistinction, tables, tablesByUuid, updateSeatsRequest, guestSearchSet, seatsByUUID
    case ActionTypes.CLEAR_SEAT: {
      const { seat } = action.payload;
      const { table_uuid: tableUUID, uuid: seatUUID } = seat;
      const { tablesByUuid } = state;
      const { table, index: tableIndex } = tablesByUuid[tableUUID];
      const clearedSeat = {
        ...seat,
        guest_id: null,
        couple_role: null,
        display_name: null,
        isEmpty: true
      };
      const newSeatArray = state.tablesByUuid[seat.table_uuid].table.seats.map(tableSeat => (tableSeat.uuid === seatUUID ? clearedSeat : tableSeat));
      const newTable = { ...table, seats: newSeatArray };
      const distinctionKey = seat.guest_id === null ? 'couple' : 'guests';
      const occupantKey = seat.guest_id === null ? seat.couple_role : seat.guest_id;
      const newDistinctionGroup = { ...state.seatsByGuestDistinction[distinctionKey] };
      delete newDistinctionGroup[occupantKey];

      // compose the return values
      const tables = state.tables.map(currentTable => (currentTable.uuid === tableUUID ? newTable : currentTable));
      const newTablesByUuid = {
        ...tablesByUuid,
        [tableUUID]: { table: newTable, index: tableIndex }
      };
      const seatsByGuestDistinction = {
        ...state.seatsByGuestDistinction,
        [distinctionKey]: newDistinctionGroup
      };
      const updateSeatsRequest = [clearedSeat];
      const guestSearchSet = (distinctionKey === 'couple') ? state.guestSearchSet
        : {
          ...state.guestSearchSet,
          [occupantKey]: {
            ...state.guestSearchSet[occupantKey],
            seatUUID: null,
            tableUUID: null,
          }
        };
      const seatsByUUID = { ...state.seatsByUUID, [seatUUID]: clearedSeat };
      return {
        ...state,
        seatsByGuestDistinction,
        tables,
        tablesByUuid: newTablesByUuid,
        updateSeatsRequest,
        guestSearchSet,
        seatsByUUID
      };
    }
    // onboarding
    case ActionTypes.TRIGGER_SEATING_ONBOARDING: {
      return {
        ...state,
        onboarding: true
      };
    }
    // onboarding
    case ActionTypes.FINISH_ONBOARDING: {
      return {
        ...state,
        onboarding: false,
        step: 0
      };
    }
    // onboarding, step
    case ActionTypes.ONBOARDING_SELECTION_STEP: {
      return {
        ...state,
        onboarding: true,
        step: 0
      };
    }
    // onboarding, step
    case ActionTypes.ONBOARDING_TABLE_STEP: {
      return {
        ...state,
        onboarding: true,
        step: 2
      };
    }
    // draggingSeatInProgress, seatsReorderedTable, selectedGuests, updateSeatsRequest
    // TODO: + guestSearchSet, seatsByUUID
    case ActionTypes.CLEAR_SELECTED_GUESTS: {
      // clear the requests state and seatsReorderedTable
      // This is hit right before the update request is sent to the backend
      const {
        selectedGuests,
        updateSeatsRequest,
        seatsReorderedTable,
        draggingSeatInProgress
      } = initialState;
      return {
        ...state,
        selectedGuests,
        updateSeatsRequest,
        seatsReorderedTable,
        draggingSeatInProgress
      };
    }
    // step
    case ActionTypes.INC_ONBOARDING_STEP: {
      return {
        ...state,
        step: state.step + 1
      };
    }
    // step
    case ActionTypes.DEC_ONBOARDING_STEP: {
      return {
        ...state,
        step: state.step - 1
      };
    }
    // event
    case ActionTypes.SET_NEW_CHART_EVENT: {
      const { event } = action.payload;
      return { ...state, event };
    }
    // initialState
    case ActionTypes.DELETE_CHART: {
      return initialState;
    }
    // selectedGuests
    case ActionTypes.SELECT_GUESTS: {
      const { guest } = action.payload;
      if (guest == null) return state;

      let guests = state.selectedGuests.guests.slice(); // Copy previous selected guests
      let couple = state.selectedGuests.couple.slice(); // Copy previous selected couple member

      if (action.payload.isCouple) {
        const coupleRole = guest.couple_role;
        const isChecked = includes(couple, coupleRole);

        if (isChecked) {
          couple = without(couple, coupleRole); // Remove the couple member from the selection
        } else {
          couple.push(coupleRole); // Add the couple member to the selection
        }
      } else {
        const guestId = guest.guest_view != null ? guest.guest_view.id : guest.guest_id; // Guest_View only exists from guest rail
        const isChecked = includes(guests, guestId);

        if (isChecked) {
          guests = without(guests, guestId); // Remove the guest from the selection
        } else {
          guests.push(guestId); // add the guest to the selection
          const { guestGroup } = action.payload;
          // If the guest group exists, we're coming from the guest rail
          if (guestGroup != null && guest.guest_view.relationship_type === 'PRIMARY') {
            // For primary guests in the guest rail, we want to select their secondary guests automatically
            for (let i = 0; i < guestGroup.guests.length; i += 1) {
              const isSeated = guestGroup.guests[i].table_uuid != null;
              const additionalGuest = guestGroup.guests[i].guest_view;

              if (additionalGuest != null) {
                const checked = includes(guests, additionalGuest.id);
                // If the secondary guest is not already checked, and not seated, add them to the selection
                if (!isSeated && !checked) {
                  guests.push(additionalGuest.id);
                }
              }
            }
          }
        }
      }

      return {
        ...state,
        selectedGuests: { couple, guests }
      };
    }
    // dragginSeatInProgress, seatsReorderedTable, selectedGuests
    case ActionTypes.DRAG_STARTED: {
      const { guest, isCouple } = action.payload;
      const { uuid: guestUUID, table_uuid: tableUUID } = guest;
      const seatsReorderedTable = (guestUUID !== null && tableUUID === null)
        ? null : state.tablesByUuid[tableUUID].table;
      let newSelectedGuests = { ...state.selectedGuests };

      if (!isEmpty(isCouple)) {
        const coupleRole = guest.couple_role;
        const isChecked = includes(state.selectedGuests.couple, coupleRole);
        if (!isChecked) {
          newSelectedGuests = {
            guests: state.selectedGuests.guests,
            couple: [...state.selectedGuests.couple, coupleRole]
          };
        }
      } else {
        const guestId = action.payload.guest.guest_view != null ? action.payload.guest.guest_view.id : action.payload.guest.guest_id; // Guest_View only exists from guest rail
        const isChecked = includes(state.selectedGuests.guests, guestId);
        if (!isChecked) {
          newSelectedGuests = {
            couple: state.selectedGuests.couple,
            guests: [...state.selectedGuests.guests, guestId]
          };
        }
      }
      return {
        ...state,
        draggingSeatInProgress: true,
        selectedGuests: newSelectedGuests,
        seatsReorderedTable
      };
    }
    // draggingSeatInProgress
    case ActionTypes.DRAG_TABLE_STARTED: {
      return {
        ...state,
        draggingSeatInProgress: false
      };
    }
    // draggingSeatInProgress, seatsReorderedTable
    case ActionTypes.DRAG_ENDED: {
      return {
        ...state,
        draggingSeatInProgress: false,
        seatsReorderedTable: null
      };
    }
    // activeSeatInput
    case ActionTypes.SET_ACTIVE_SEAT_INPUT: {
      const { seatUUID } = action.payload;
      return { ...state, activeSeatInput: seatUUID };
    }
    // busy
    case ActionTypes.HIDE_SPINNER: {
      return { ...state, busy: false };
    }

    default:
      return state;
  }
};

export default seatingChartReducer;



// WEBPACK FOOTER //
// ./src/reducers/seatingChart.js